import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Edit,
  Eye,
  EyeOff,
  FileSpreadsheet,
  Image,
  Package2,
  PackageCheck,
  PackageX,
  Plus,
  Search,
  Trash2,
} from "lucide-react";
import React from "react";
import { toast } from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { Badge } from "../../components/ui/Badge";
import { Button } from "../../components/ui/Button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/Card";
import { Input } from "../../components/ui/Input";
import { Spinner } from "../../components/ui/Spinner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/Table";
import { fetchWithAuth } from "../../hooks/useAuth";
import { useProducts } from "../../hooks/useProducts";
import { formatPrice } from "../../utils/format";
import { AddProductPannel } from "./AddProductPannel";

import { ProductsStockPannel } from "./ProductsStockPannel";

// Product type imported from reusable hook

export function ProductsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [search, setSearch] = React.useState("");
  const [isCreating, setIsCreating] = React.useState(false);
  const [deletingId, setDeletingId] = React.useState<number | null>(null);
  const [togglingId, setTogglingId] = React.useState<number | null>(null);

  // Categories moved to ProductCategoryPannel
  // Fetch products via reusable hook
  const { data: products, isLoading } = useProducts();

  console.log(products);

  // Case-insensitive client-side filtering by product name
  const filteredProducts = React.useMemo(() => {
    const list = products || [];
    const term = search.trim().toLowerCase();
    if (!term) return list;
    return list.filter((p) =>
      (p.title || p.name || "").toLowerCase().includes(term)
    );
  }, [products, search]);

  // Fetch product statistics
  const { data: stats } = useQuery({
    queryKey: ["product-stats"],
    queryFn: async () => {
      const response = await fetchWithAuth("/api/admin/products/stats");
      if (!response.ok) throw new Error("Eroare la încărcarea statisticilor");
      return response.json() as Promise<{
        totalProducts: number;
        totalStock: number;
        lowStock: number;
        outOfStock: number;
      }>;
    },
  });

  // Categories moved to ProductCategoryPannel

  // Delete product mutation
  const deleteProduct = useMutation({
    mutationFn: async (id: number) => {
      const response = await fetchWithAuth(`/api/admin/products/${id}`, {
        method: "DELETE",
      });

      if (!response.ok) {
        const data = await response.json();
        if (response.status === 400) {
          throw new Error(
            data.message ||
              "Nu se poate șterge produsul deoarece există comenzi asociate"
          );
        }
        throw new Error("Eroare la ștergerea produsului");
      }

      return response.json();
    },
    onMutate: (id: number) => {
      setDeletingId(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["products"] });
      toast.success("Produs șters cu succes");
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
    onSettled: () => {
      setDeletingId(null);
    },
  });

  // Toggle product visibility mutation
  const toggleVisibility = useMutation({
    mutationFn: async (id: number) => {
      const response = await fetchWithAuth(
        `/api/admin/products/${id}/toggle-visibility`,
        {
          method: "PATCH",
        }
      );

      if (!response.ok) {
        const data = await response.json();
        throw new Error(
          data.message || "Eroare la schimbarea vizibilității produsului"
        );
      }

      return response.json();
    },
    onMutate: (id: number) => {
      setTogglingId(id);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["products"] });
      toast.success(data.message);
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
    onSettled: () => {
      setTogglingId(null);
    },
  });

  // Category mutations
  /* Categories moved to ProductCategoryPannel: create */
  /* const createCategory = useMutation({
    mutationFn: async (data: typeof categoryFormData) => {
      const response = await fetchWithAuth("/api/admin/categories", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Eroare la crearea categoriei");
      }
      const result = await response.json();
      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["categories"] });
      toast.success("Categorie creată cu succes");
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  }); */

  /* Categories moved to ProductCategoryPannel: update */
  /* const updateCategory = useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: number;
      data: Partial<typeof categoryFormData>;
    }) => {
      const response = await fetchWithAuth(`/api/admin/categories/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message || "Eroare la actualizarea categoriei"
        );
      }
      const result = await response.json();
      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["categories"] });
      toast.success("Categorie actualizată cu succes");
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  }); */

  /* Categories moved to ProductCategoryPannel: delete */
  /* const deleteCategory = useMutation({
    mutationFn: async (id: number) => {
      const response = await fetchWithAuth(`/api/admin/categories/${id}`, {
        method: "DELETE",
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Eroare la ștergerea categoriei");
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["categories"] });
      toast.success("Categorie ștearsă cu succes");
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  }); */

  const handleDelete = async (id: number) => {
    if (
      window.confirm(
        "Sigur doriți să ștergeți acest produs? Această acțiune nu poate fi anulată."
      )
    ) {
      deleteProduct.mutate(id);
    }
  };

  // Visibility handlers
  const handleToggleVisibility = (id: number) => {
    toggleVisibility.mutate(id);
  };

  // Category handlers moved to ProductCategoryPannel

  return (
    <div className="p-6 space-y-8 bg-background min-h-screen">
      {/* Header */}
      <Card className="flex justify-between items-center">
        <div className="flex items-center gap-2">
          <Package2 className="w-8 h-8 text-blue-600" />
          <div>
            <h1 className="text-2xl font-bold text-foreground">Produse</h1>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <Button
            className="flex items-center gap-2 bg-green-600 hover:bg-green-700"
            onClick={() => setIsCreating(true)}
          >
            <Plus className="w-4 h-4" />
            Adaugă Produs
          </Button>
          <Button
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700"
            onClick={async () => {
              try {
                const response = await fetchWithAuth(
                  "/api/admin/products/export",
                  {
                    method: "GET",
                    headers: {
                      Accept:
                        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                    },
                  }
                );
                if (!response.ok) {
                  throw new Error("Eroare la exportul produselor");
                }
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = `produse-${
                  new Date().toISOString().split("T")[0]
                }.xlsx`;
                document.body.appendChild(a);
                a.click();
                window.URL.revokeObjectURL(url);
                document.body.removeChild(a);
              } catch (error) {
                console.error("Export error:", error);
              }
            }}
          >
            <FileSpreadsheet className="w-4 h-4" />
            Export Excel
          </Button>
        </div>
      </Card>

      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card className="">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
              <Package2 className="w-4 h-4" />
              Total Produse
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-foreground">
              {stats?.totalProducts || 0}
            </div>
          </CardContent>
        </Card>
        <Card className="">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
              <Package2 className="w-4 h-4" />
              Stoc Total
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-foreground">
              {stats?.totalStock || 0}
            </div>
          </CardContent>
        </Card>
        <Card className="">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
              <PackageCheck className="w-4 h-4" />
              Stoc Redus
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-foreground">
              {stats?.lowStock || 0}
            </div>
          </CardContent>
        </Card>
        <Card className="">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
              <PackageX className="w-4 h-4" />
              Stoc Epuizat
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-foreground">
              {stats?.outOfStock || 0}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Create Product Form */}
      {isCreating && (
        <AddProductPannel
          onSuccess={() => setIsCreating(false)}
          onCancel={() => setIsCreating(false)}
        />
      )}

      {/* Stocks Panel */}
      <ProductsStockPannel />

      {/* Search and Quick Actions */}
      <Card className="">
        <div className="space-y-4">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-3 text-muted-foreground" />
            <Input
              className="pl-9"
              placeholder="Caută după nume..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          {/* Quick Actions Info */}
          <Card className="flex items-center justify-between p-3">
            <div className="flex items-center gap-2">
              <Eye className="w-4 h-4 text-blue-600" />
              <span className="text-sm font-medium text-blue-800">
                Acțiuni Rapide de Vizibilitate
              </span>
            </div>
            <span className="text-xs text-blue-600">
              Folosește butoanele din tabel pentru a schimba rapid vizibilitatea
              produselor
            </span>
          </Card>
        </div>
      </Card>

      {/* Products Table */}
      <Card className=" overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="font-semibold">ID</TableHead>
                <TableHead className="font-semibold">Nume</TableHead>
                <TableHead className="font-semibold">Preț</TableHead>
                <TableHead className="font-semibold">Stoc</TableHead>
                <TableHead className="font-semibold">Vizibilitate</TableHead>
                <TableHead className="font-semibold">Status</TableHead>
                <TableHead className="font-semibold">Acțiuni</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-10">
                    <div className="flex items-center justify-center text-muted-foreground">
                      Se încarcă...
                    </div>
                  </TableCell>
                </TableRow>
              ) : filteredProducts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-10">
                    <div className="text-muted-foreground">
                      Nu există produse
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                filteredProducts.map((product) => (
                  <TableRow key={product.id} className="">
                    <TableCell className="font-medium">#{product.id}</TableCell>
                    <TableCell>
                      <div className="flex flex-col">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-foreground">
                            {product.title || product.name}
                          </span>
                          {product.images && product.images.length > 0 && (
                            <Badge className="bg-blue-100 text-blue-800 ring-1 ring-blue-200 text-xs">
                              <Image className="w-3 h-3 mr-1" />
                              {product.images.length}
                            </Badge>
                          )}
                        </div>
                        <span className="text-sm text-muted-foreground truncate max-w-md">
                          {product.description}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="font-medium">
                      {product.priceInfo?.hasMultiplePrices ? (
                        <div className="flex flex-col">
                          <span className="text-sm text-muted-foreground">
                            de la
                          </span>
                          <span>
                            {formatPrice(product.priceInfo.minPrice || 0)}
                          </span>
                        </div>
                      ) : (
                        formatPrice(product.priceInfo?.price || 0)
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={
                          (product.stock || 0) > 10
                            ? "success"
                            : (product.stock || 0) > 0
                            ? "warning"
                            : "destructive"
                        }
                      >
                        {product.stock || 0} bucăți
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={
                          (product.status && product.status !== "ACTIVE") ||
                          product.hide
                            ? "outline"
                            : "success"
                        }
                      >
                        {(product.status && product.status !== "ACTIVE") ||
                        product.hide ? (
                          <div className="flex items-center gap-1">
                            <EyeOff className="w-3 h-3" />
                            Ascuns
                          </div>
                        ) : (
                          <div className="flex items-center gap-1">
                            <Eye className="w-3 h-3" />
                            Vizibil
                          </div>
                        )}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={
                          (product.stock || 0) > 10
                            ? "success"
                            : (product.stock || 0) > 0
                            ? "warning"
                            : "destructive"
                        }
                      >
                        {(product.stock || 0) > 10
                          ? "În Stoc"
                          : (product.stock || 0) > 0
                          ? "Stoc Limitat"
                          : "Stoc Epuizat"}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2 flex-wrap">
                        {/* Quick visibility toggle button */}
                        <Button
                          variant="outline"
                          size="sm"
                          className={`flex items-center gap-1 ${
                            (product.status && product.status !== "ACTIVE") ||
                            product.hide
                              ? "text-green-600 hover:text-green-700 hover:bg-green-50"
                              : "text-muted-foreground hover:text-muted-foreground hover:bg-gray-50"
                          }`}
                          onClick={() => handleToggleVisibility(product.id)}
                          disabled={
                            togglingId === product.id &&
                            toggleVisibility.isPending
                          }
                          title={
                            (product.status && product.status !== "ACTIVE") ||
                            product.hide
                              ? "Afișează produsul"
                              : "Ascunde produsul"
                          }
                        >
                          {togglingId === product.id &&
                          toggleVisibility.isPending ? (
                            <>
                              <Spinner className="w-4 h-4" />
                              <span>Se schimbă...</span>
                            </>
                          ) : (
                            <>
                              {(product.status &&
                                product.status !== "ACTIVE") ||
                              product.hide ? (
                                <Eye className="w-4 h-4" />
                              ) : (
                                <EyeOff className="w-4 h-4" />
                              )}
                              {(product.status &&
                                product.status !== "ACTIVE") ||
                              product.hide
                                ? "Afișează"
                                : "Ascunde"}
                            </>
                          )}
                        </Button>

                        <Button
                          variant="outline"
                          size="sm"
                          className="flex items-center gap-1"
                          onClick={() => navigate(`/products/${product.id}/`)}
                        >
                          <Edit className="w-4 h-4" />
                          Editează
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex items-center gap-1 text-red-600 hover:text-red-700"
                          onClick={() => handleDelete(product.id)}
                          disabled={
                            deletingId === product.id && deleteProduct.isPending
                          }
                        >
                          {deletingId === product.id &&
                          deleteProduct.isPending ? (
                            <>
                              <Spinner className="mr-2" /> Ștergere...
                            </>
                          ) : (
                            <>
                              <Trash2 className="w-4 h-4" />
                              Șterge
                            </>
                          )}
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </Card>
    </div>
  );
}
